import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lightning } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Header, Disclaimer } from "../components/Header";
import { TIERS, BillingToggle, startCheckout } from "../components/PaywallDialog";

const FREE_PERKS = ["1 parlay build per day", "NFL + NBA", "AI coach's notes on every ticket", "Save parlays + track W/L"];

export default function Pricing() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");

  const choosePaid = (lookupKey) => {
    if (user) startCheckout(API, lookupKey);
    else navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg">
      <Header />
      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-14" data-testid="pricing-page">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E676] mb-2">Pricing</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
            Pick your lane
          </h1>
          <BillingToggle cycle={cycle} setCycle={setCycle} />
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <div className="rounded-xl border border-white/10 bg-[#141419] p-6 flex flex-col" data-testid="pricing-tier-free">
            <p className="font-display text-2xl font-black uppercase tracking-tight flex items-center gap-1.5 text-slate-300 mb-1">
              <Lightning size={20} weight="fill" /> Free
            </p>
            <p className="text-sm text-slate-400 mb-1"><span className="text-white font-bold text-lg">$0</span>/forever</p>
            <p className="mb-3" />
            <ul className="space-y-2 mb-6 flex-1">
              {FREE_PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-xs text-slate-300">
                  <Check size={14} weight="bold" className="shrink-0 mt-0.5 text-slate-500" /> {p}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate(user ? "/app" : "/auth")} data-testid="pricing-free-btn"
              className="w-full rounded-full bg-white/10 hover:bg-white/20 font-bold py-2.5 text-sm transition-colors">
              {user ? "Open the builder" : "Start free"}
            </button>
          </div>

          {TIERS.map(({ name, color, Icon, badge, perks, ...tier }) => {
            const p = tier[cycle];
            return (
              <div key={name} className="rounded-xl border p-6 flex flex-col" data-testid={`pricing-tier-${name.toLowerCase()}`}
                style={{ borderColor: `${color}55`, background: `${color}0d` }}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-display text-2xl font-black uppercase tracking-tight flex items-center gap-1.5" style={{ color }}>
                    <Icon size={20} weight="fill" /> {name}
                  </p>
                  {badge && <span className="text-[10px] font-black uppercase rounded-full px-2 py-0.5" style={{ background: color, color: "#000" }}>{badge}</span>}
                </div>
                <p className="text-sm text-slate-400 mb-1"><span className="text-white font-bold text-lg">{p.price}</span>{p.per}</p>
                {p.save ? <p className="text-[11px] font-bold text-[#00E676] mb-3">{p.save}</p> : <p className="mb-3" />}
                <ul className="space-y-2 mb-6 flex-1">
                  {perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-xs text-slate-300">
                      <Check size={14} weight="bold" className="shrink-0 mt-0.5" style={{ color }} /> {perk}
                    </li>
                  ))}
                </ul>
                <button onClick={() => choosePaid(p.key)} data-testid={`pricing-${name.toLowerCase()}-btn`}
                  className="w-full rounded-full font-bold py-2.5 text-sm hover:scale-[1.02] active:scale-95 transition-transform"
                  style={{ background: color, color: "#000" }}>
                  Get {name}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 mt-8 max-w-2xl">
          All plans are software subscriptions for sports analytics and entertainment — we are not a sportsbook and
          never handle wagers. Cancel anytime from the billing portal; paid access runs to the end of the billing period.
        </p>
      </main>
      <Disclaimer />
    </div>
  );
}
